import React, { useContext, useEffect, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Link as MuiLink, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import { useLocation } from 'react-router';

const activeStyle = {
  background: 'rgba(0,0,0,.05)',
};

export interface IAdminMenuLink {
  label: string;
  to?: string;
  onClick?: () => void;
}

export type RegisterAdminMenuLink = (link: IAdminMenuLink) => () => void;

// Lets the omnibar offer exactly the links the admin menu shows, so the two cannot drift apart.
const AdminMenuLinkCollectorContext = React.createContext<RegisterAdminMenuLink | undefined>(undefined);

export const AdminMenuLinkCollector = AdminMenuLinkCollectorContext.Provider;

function useCollectedAdminMenuLink(children: React.ReactNode, to?: string, onClick?: () => void) {
  const register = useContext(AdminMenuLinkCollectorContext);
  const onClickRef = useRef(onClick);
  onClickRef.current = onClick;
  const label = typeof children === 'string' ? children : undefined;
  const hasOnClick = !!onClick;

  useEffect(() => {
    if (!register || !label) return undefined;
    return register({ label, to: to || undefined, onClick: hasOnClick ? () => onClickRef.current?.() : undefined });
  }, [register, label, to, hasOnClick]);

  return !!register;
}

export function AdminLink({ children, to = '', onClick }: { children: React.ReactNode; to?: string; onClick?: () => void }) {
  const isCollected = useCollectedAdminMenuLink(children, to, onClick);
  const location = useLocation();
  const extraStyle = location.pathname.indexOf(to) > -1 && to !== '' ? activeStyle : {};

  function handleClick(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) {
    if (onClick) {
      event.preventDefault();
      event.stopPropagation();
      onClick();
    }
  }

  if (isCollected) return null;

  return (
    <Box sx={{ lineHeight: 1.5, px: 2, ...extraStyle }}>
      <MuiLink underline="none" color="#333" component={RouterLink} to={to} onClick={handleClick}>
        {children}
      </MuiLink>
    </Box>
  );
}

export function AdminHeaderLink({ children, to }: { children?: React.ReactNode; to: string }) {
  const isCollected = useCollectedAdminMenuLink(children, to);
  const location = useLocation();
  const extraStyle = location.pathname.indexOf(to) > -1 && to !== '' ? activeStyle : {};

  if (isCollected) return null;

  return (
    <Box sx={{ lineHeight: 1, px: 2, pb: '2px', marginTop: 2, ...extraStyle }}>
      <MuiLink underline="none" color="#333" component={RouterLink} to={to}>
        <Typography variant="h3">{children}</Typography>
      </MuiLink>
    </Box>
  );
}

export function AdminRouterLink({ children, to }: { children?: React.ReactNode; to: string }) {
  const isCollected = useCollectedAdminMenuLink(children, to);
  const location = useLocation();
  const extraStyle = location.pathname.indexOf(to) > -1 && to !== '' ? activeStyle : {};

  if (isCollected) return null;

  return (
    <Box sx={{ lineHeight: 1.5, px: 2, ...extraStyle }}>
      <MuiLink underline="none" color="#333" component={RouterLink} to={to}>
        {children}
      </MuiLink>
    </Box>
  );
}
